import { CheckCircle2 } from "lucide-react";

import { ApiError } from "../../api/client";
import type { CoherenceIssue, Order, OrderFieldSpecs } from "../../api/types";
import { DOSSIER_EXPORT, ORDER_APPROVE } from "../../app/dossierState";
import { attentionSelector, designate, designateSoon } from "../../components/attention";
import { Button } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { t, tServer } from "../../i18n/i18n";
import { plural } from "../../plural";
import type { Notice } from "../../state/ui";

/** Validation de l'OPO (demande d'Alexis du 12/10/2026) : l'OPO généré est un brouillon que l'utilisateur
 *  relit et modifie ; « Valider l'OPO » le fige (lecture seule) et ouvre le téléchargement du dossier
 *  d'exercice complet ; « Rouvrir » le remet en brouillon. « Valider l'OPO » est l'action attendue tant
 *  que l'OPO est en brouillon, et il est désigné après chaque génération et chaque modification
 *  enregistrée. */

type Notify = (notice: Omit<Notice, "id">) => void;

/** Ce qui décide si « Valider l'OPO » est l'action attendue. */
export interface ApprovalState {
  finalized: boolean;
  generating: boolean;
  approving: boolean;
  saveStatus: "saved" | "saving" | "retrying" | "blocked";
  stale: boolean;
  locked: boolean;
  languageMismatch: boolean;
  /** Relecture d'abord : paragraphes mis à jour d'après le dossier pas encore relus sur ce poste (« Suivant »
   *  jusqu'au dernier, ou « Masquer les repères »), ou paragraphes modifiés à la main à revoir. Absent : aucune. */
  reviewPending?: boolean;
  /** Signature à refaire (texte changé depuis la signature) : « Signer de nouveau » passe avant, une seule action
   *  désignée à la fois. Absent : aucune. */
  signatureOutdated?: boolean;
}

/** Brouillon enregistré : « Valider l'OPO » pulse, sauf pendant une génération ou un enregistrement, ou
 *  quand une autre action passe avant (régénérer dans la langue du dossier, terrain à préparer, dossier en
 *  lecture seule, paragraphes à relire : c'est alors « Suivant » ou la pastille « à revoir » qui est désigné ;
 *  signature à refaire : « Signer de nouveau »). */
export function approvalExpected(s: ApprovalState): boolean {
  return (
    !s.finalized &&
    !s.generating &&
    !s.approving &&
    s.saveStatus === "saved" &&
    !s.stale &&
    !s.locked &&
    !s.languageMismatch &&
    !s.reviewPending &&
    !s.signatureOutdated
  );
}

/** OPO rédigé ou réécrit (génération, régénération, WINGO) : en brouillon, « Valider l'OPO » est désigné
 *  une fois la vue à jour. Rend vrai s'il l'a été. */
export function designateAfterWrite(order: Pick<Order, "status">): boolean {
  if (order.status === "finalized") return false;
  designateSoon(attentionSelector(ORDER_APPROVE), "auto", 2500);
  return true;
}

/** Suivi des enregistrements de l'éditeur : `settled` quand un envoi vient d'aboutir (« saving » puis «
 *  saved »). */
export function saveSettled(
  seen: boolean,
  status: ApprovalState["saveStatus"],
): { seen: boolean; settled: boolean } {
  if (status === "saving") return { seen: true, settled: false };
  if (status === "saved" && seen) return { seen: false, settled: true };
  return { seen, settled: false };
}

/** Modification enregistrée : « Valider l'OPO » redevient l'action attendue (absent si l'OPO est validé). */
export function designateApproval(): HTMLElement | null {
  return designate(attentionSelector(ORDER_APPROVE));
}

/** Refus de la validation par le contrôle de l'ordre : premier point bloquant et le paragraphe à ouvrir
 *  (celui que le contrôle nomme, sinon le premier de l'onglet). Null pour tout autre échec. */
export function blockingIssue(
  err: unknown,
  specs: OrderFieldSpecs | undefined,
): { issue: CoherenceIssue; field: string | null } | null {
  if (!(err instanceof ApiError) || err.code !== "ORDER_INCOMPLETE") return null;
  const issues = err.detail?.issues;
  const issue = Array.isArray(issues) ? (issues[0] as CoherenceIssue | undefined) : undefined;
  if (!issue) return null;
  const field = issue.field ?? specs?.fields.find((f) => f.tab === issue.page)?.key ?? null;
  return { issue, field };
}

/** Point bloquant : le paragraphe (déjà ouvert dans l'éditeur par l'appelant) est désigné, l'avis dit
 *  lequel. */
export function designateIssue(
  blocking: { issue: CoherenceIssue; field: string | null },
  notify: Notify,
  oplan = false,
): void {
  if (blocking.field) designateSoon(`[id="field-${blocking.field}"]`, "field");
  const point = tServer(blocking.issue.description);
  notify({ text: oplan ? t("OPLAN non validé : {point}", { point }) : t("OPO non validé : {point}", { point }), tone: "info" });
}

/** OPLAN du corps validé (docs/08 §25) : l'OPO du corps, qui en est tiré, est l'étape suivante ; l'avis propose de
 *  l'ouvrir. */
export function announceOplanApproved(notify: Notify, openOrder: () => void): void {
  notify({
    text: t("OPLAN validé : l'ordre d'opérations du corps, qui en est tiré, est à relire et à valider à son tour."),
    tone: "ok",
    action: { label: t("Ouvrir l'OPO"), run: openOrder },
  });
}

/** OPO validé : avis, et l'icône de téléchargement du conteneur « Dossier d'exercice » désignée quand le
 *  dossier complet est prêt (fiche pays et mise en ambiance rédigées). Rend vrai si le dossier est prêt. Licence
 *  d'essai (registre F44) : le dossier ne se télécharge pas, l'avis dit seulement qu'il est complet. */
export function announceApproved(country: boolean, briefing: boolean, notify: Notify, trial = false): boolean {
  if (country && briefing) {
    if (trial) {
      notify({ text: t("OPO validé : le dossier d'exercice est complet."), tone: "ok" });
      return true;
    }
    notify({ text: t("OPO validé : le dossier d'exercice est prêt à être téléchargé."), tone: "ok" });
    designateSoon(attentionSelector(DOSSIER_EXPORT));
    return true;
  }
  notify({
    text: country
      ? t("OPO validé : rédigez encore la mise en ambiance pour compléter le dossier d'exercice.")
      : t("OPO validé : rédigez encore la fiche pays pour compléter le dossier d'exercice."),
    tone: "info",
  });
  return false;
}

/** « Suivant » du bandeau des paragraphes mis à jour : relecture des zones surlignées, une à une. */
export const ORDER_CHANGES_NEXT = "order-changes-next";

/** OPO rouvert : de retour en brouillon, recomposé d'après le dossier. Des paragraphes ont changé depuis la
 *  validation (`changed`) : l'avis le dit et « Suivant » est désigné (relecture d'abord) ; sinon « Valider l'OPO »
 *  redevient l'action attendue. */
export function announceReopened(notify: Notify, changed = 0, detail?: string, oplan = false): void {
  if (changed > 0) {
    const paragraphs = plural(changed, t("paragraphe surligné"), t("paragraphes surlignés"));
    notify({
      text: oplan
        ? t("OPLAN rouvert et mis à jour d'après le dossier : {paragraphs}. Relisez-les, puis validez-le de nouveau.", { paragraphs })
        : t("OPO rouvert et mis à jour d'après le dossier : {paragraphs}. Relisez-les, puis validez-le de nouveau.", { paragraphs }),
      detail: detail || undefined,
      tone: "info",
    });
    designateSoon(attentionSelector(ORDER_CHANGES_NEXT), "auto", 2500);
    return;
  }
  notify({
    text: oplan
      ? t("L'OPLAN repasse en brouillon : validez-le de nouveau quand il est relu.")
      : t("L'OPO repasse en brouillon : validez-le de nouveau pour télécharger le dossier complet."),
    detail: detail || undefined,
    tone: "info",
  });
  designateSoon(attentionSelector(ORDER_APPROVE));
}

/** Bouton « Valider l'OPO » de la barre de l'ordre, libellé aussi sur tablette. */
export function ApproveButton({
  onApprove,
  pending,
  disabled,
  expected,
  blocked,
  onBlocked,
  oplan = false,
  label,
  variant = "primary",
}: {
  onApprove: () => void;
  pending: boolean;
  disabled: boolean;
  expected: boolean;
  blocked?: boolean;
  onBlocked?: () => void;
  /** OPLAN du corps (docs/08 §25) : « Valider l'OPLAN ». */
  oplan?: boolean;
  /** Libellé explicite (rotation CENTAC : « Valider l'OPO N°2 »). */
  label?: string;
  /** Rotation CENTAC, OPO validé hors de son tour : bouton secondaire (l'action en avant est celle de l'étape). */
  variant?: "primary" | "secondary";
}) {
  return (
    <Button
      size="sm"
      variant={variant}
      data-attention={ORDER_APPROVE}
      onClick={onApprove}
      disabled={pending || disabled}
      highlight={expected}
      blocked={blocked}
      onBlocked={onBlocked}
      title={
        oplan
          ? t("Valider l'OPLAN relu : il passe en lecture seule, l'OPO du corps en est tiré")
          : t("Valider l'OPO relu : il passe en lecture seule, le dossier complet se télécharge")
      }
    >
      <CheckCircle2 size={14} /> {pending ? <Waiting>{t("Validation…")}</Waiting> : label ?? (oplan ? t("Valider l'OPLAN") : t("Valider l'OPO"))}
    </Button>
  );
}
